import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Network,
  Save,
  Info,
  CheckCircle2,
  ExternalLink,
  RotateCw,
  Mail,
  AtSign,
  Lock,
  Inbox,
} from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import {
  profileService,
  type ProfileResponse,
  type LegalEntityHierarchy,
  type BranchHierarchy,
  type ChannelImapSettings,
  type ChannelSmtpSettings,
} from "@/services/profileService";
import { communicationChannelsService } from "@/services/communicationChannelsService";
import { crmGatewaySyncService } from "@/services/crmGatewaySyncService";
import { chatGatewaySyncService } from "@/services/chatGatewaySyncService";
import { useChatGatewaySyncStore } from "@/store/chatGatewaySyncStore";
import { useUiStore } from "@/store/uiStore";
import { buildChatwootInboxesUrl, fetchChatwootInboxesUrl } from "@/config/chatwoot";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";
import { useSettingsStore } from "@/store/settingsStore";
import { useProfileStore } from "@/store/profileStore";
import { useDisplayLegalEntities } from "@/hooks/useDisplayLegalEntities";
import { ChannelEntityCardHeader } from "./communicationChannels/ChannelEntityCardHeader";
import { toast } from "react-toastify";
import { showActionError } from "@/lib/feedback";
import { Tooltip } from "@/components/atoms/Tooltip";

const CHANNEL_LOCKED_MESSAGE =
  "These email details are locked. Please contact the support team to change them.";

const notifyChannelLocked = () => toast.info(CHANNEL_LOCKED_MESSAGE, { autoClose: 4000 });

const LockedFieldValue: React.FC<{ value: string }> = ({ value }) => (
  <Tooltip content={CHANNEL_LOCKED_MESSAGE} side="top">
    <button
      type="button"
      onClick={notifyChannelLocked}
      className="h-10 w-full inline-flex items-center justify-between gap-2 px-3 text-left bg-slate-100 border border-bridge-border rounded-lg text-xs text-bridge-fg cursor-not-allowed hover:bg-slate-200 transition-colors"
    >
      <span className="truncate">{value || "—"}</span>
      <Lock className="w-4 h-4 text-bridge-muted shrink-0" />
    </button>
  </Tooltip>
);

/**
 * Field with a tiny uppercase label above the control — mirrors the WhatsApp
 * configuration form so the Email channel feels visually consistent.
 */
const Field: React.FC<{
  label: string;
  required?: boolean;
  hint?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-1.5 min-w-0">
    <label className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider flex items-center gap-1">
      {label}
      {required && <span className="text-red-500 normal-case">*</span>}
    </label>
    {children}
    {hint && <div className="text-[11px] text-bridge-muted">{hint}</div>}
  </div>
);

interface ImapSettings {
  address: string;
  port: string;
  enableSsl: boolean;
}

interface SmtpSettings {
  address: string;
  port: string;
  domain: string;
  encryption: string;
  opensslVerifyMode: string;
  authentication: string;
}

/** Per-entity email form values held client-side until the backend lands. */
interface EmailConfig {
  inboxLabel: string;
  emailId: string;
  imap: ImapSettings;
  smtp: SmtpSettings;
  enabled: boolean;
  /** Flips true after a successful Save (UI-only for now). */
  configured: boolean;
  /** Flips true after Connect (UI-only for now). */
  connected: boolean;
  /** Flips true after Enable Conversations / Resync (UI-only for now). */
  synced: boolean;
}

const GENERIC_ERROR = "Something went wrong. Please try again.";

/** How many /chatgateway/branch calls Save fires at once. */
const BRANCH_SYNC_BATCH_SIZE = 5;

/**
 * Errors from the save / sync chain must never pass silently: the feedback
 * dialog suppresses messages the sanitizer strips to nothing (purely technical
 * stacks, empty gateway bodies), so fall back to a toast whenever no dialog
 * ends up on screen.
 */
const reportError = (err: unknown, fallback: string, title?: string) => {
  const e = err as { response?: { data?: { message?: string } }; message?: string } | null;
  const raw = e?.response?.data?.message || e?.message || "";
  console.error(fallback, err);

  if (showActionError(raw || fallback, title ? { title } : undefined)) return;
  toast.error(raw.trim() ? `${fallback}: ${raw.trim()}` : `${fallback}. ${GENERIC_ERROR}`);
};

const emptyConfig = (): EmailConfig => ({
  inboxLabel: "",
  emailId: "",
  imap: {
    address: "",
    port: "",
    enableSsl: true,
  },
  smtp: {
    address: "",
    port: "",
    domain: "",
    encryption: "ssl/tls",
    opensslVerifyMode: "peer",
    authentication: "login",
  },
  enabled: false,
  configured: false,
  connected: false,
  synced: false,
});

/**
 * Email channel configuration screen. The layout intentionally mirrors the
 * WhatsApp channel (CommunicationChannelList) so the two feel like siblings.
 *
 * NOTE: This is a UI-only implementation. All form values and action states
 * live in local component state. Backend wiring (load/upsert/connect/sync)
 * should be slotted into the marked TODOs once the API is ready.
 */
export const EmailChannelList: React.FC = () => {
  const navigate = useNavigate();
  const setChatwootTarget = useUiStore((s) => s.setChatwootTarget);
  const startPageLoading = useUiStore((s) => s.startPageLoading);
  const stopPageLoading = useUiStore((s) => s.stopPageLoading);
  const { keycloak } = useKeycloak();
  const { legalEntityId } = useSettingsStore();
  const email = keycloak?.tokenParsed?.email as string | undefined;
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const isExpanded = (id: string) => expandedIds.has(id);
  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const startSync = useChatGatewaySyncStore((s) => s.startSync);

  // Per-row email config, keyed by entity id (LE or branch).
  const [configs, setConfigs] = useState<Record<string, EmailConfig>>({});

  const getConfig = (id: string): EmailConfig => configs[id] ?? emptyConfig();

  const patchConfig = (id: string, patch: Partial<EmailConfig>) => {
    setConfigs((prev) => ({ ...prev, [id]: { ...emptyConfig(), ...prev[id], ...patch } }));
  };

  useEffect(() => {
    if (!email) return;
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, legalEntityId]);

  const loadProfile = async () => {
    try {
      const p = await profileService.get(email!, legalEntityId);
      setProfile(p);
      useProfileStore.getState().applyProfileData(p);

      setConfigs(buildSeededConfigs(p));
    } catch (err) {
      reportError(err, "Failed to load hierarchy");
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const getEmailError = (value: string) => {
    const v = value.trim();
    if (!v) return null;
    if (!isValidEmail(v)) return "Enter a valid email address (e.g. support@company.com).";
    return null;
  };

  const hasRequiredFields = (cfg: EmailConfig) =>
    !!(cfg.emailId.trim() && !getEmailError(cfg.emailId));

  /** Walk the LE tree (incl. nested sub-entities) to find a legal entity by id. */
  const findLegalEntity = (leId?: string | null): LegalEntityHierarchy | undefined => {
    const id = leId?.trim();
    if (!id) return undefined;

    const walk = (list: LegalEntityHierarchy[]): LegalEntityHierarchy | undefined => {
      for (const le of list) {
        if (le.id === id) return le;
        const found = walk(le.subEntities ?? []);
        if (found) return found;
      }
      return undefined;
    };
    return walk(profile?.legalEntities ?? []);
  };

  const displayEntities = useDisplayLegalEntities(profile?.legalEntities, legalEntityId);

  const buildSeededConfigs = (p: ProfileResponse): Record<string, EmailConfig> => {
    const seeded: Record<string, EmailConfig> = {};
    const seed = (
      id: string,
      src: {
        emailId?: string | null;
        emailEnabled?: boolean | null;
        inboxLabel?: string | null;
        channelActive?: boolean | null;
        imap?: ChannelImapSettings | null;
        smtp?: ChannelSmtpSettings | null;
      },
    ) => {
      const base = emptyConfig();
      const active = !!src.channelActive;
      seeded[id] = {
        ...base,
        inboxLabel: src.inboxLabel ?? "",
        emailId: src.emailId ?? "",
        imap: {
          address: src.imap?.address ?? "",
          port: src.imap?.port != null ? String(src.imap.port) : "",
          enableSsl: src.imap?.enableSsl ?? true,
        },
        smtp: {
          address: src.smtp?.address ?? "",
          port: src.smtp?.port != null ? String(src.smtp.port) : "",
          domain: src.smtp?.domain ?? "",
          encryption: src.smtp?.encryption ?? base.smtp.encryption,
          opensslVerifyMode: src.smtp?.opensslVerifyMode ?? base.smtp.opensslVerifyMode,
          authentication: src.smtp?.authentication ?? base.smtp.authentication,
        },
        enabled: !!src.emailEnabled,
        configured: !!src.emailId,
        connected: active,
        synced: active,
      };
    };
    const walkLe = (le: LegalEntityHierarchy) => {
      seed(le.id, le);
      le.branches.forEach((b) => seed(b.id, b));
      le.subEntities?.forEach(walkLe);
    };
    p.legalEntities.forEach(walkLe);
    return seeded;
  };

  const mergeSeededConfigs = (
    seeded: Record<string, EmailConfig>,
    prev: Record<string, EmailConfig>,
  ): Record<string, EmailConfig> => {
    const merged = { ...seeded };
    for (const [id, prevCfg] of Object.entries(prev)) {
      const next = merged[id];
      if (!next) continue;
      merged[id] = {
        ...next,
        inboxLabel: next.inboxLabel || prevCfg.inboxLabel,
        emailId: next.emailId || prevCfg.emailId,
      };
    }
    return merged;
  };

  /**
   * Org-structure half of the chat-gateway flow: legal entity → every branch
   * under this row → users. Deliberately stops short of the channel
   * registration, which belongs to Enable Conversations / Resync.
   *
   * Awaited inline (not routed through the sync-job store) so the whole Save
   * sits behind the global page loader instead of a floating progress panel.
   */
  const syncStructure = async (opts: {
    legalEntityId: string;
    branchId: string | null;
    parentLegalEntityId: string | null;
  }) => {
    const le = findLegalEntity(opts?.legalEntityId);
    if (!le) {
      throw new Error("Legal entity not found in profile hierarchy.");
    }

    const subtree = getAllDescendantBranches(le);
    const branches = opts.branchId ? subtree.filter((b) => b.id === opts.branchId) : subtree;

    await chatGatewaySyncService.syncLegalEntity({
      id: le.id,
      legalName: le.name,
      primaryEmail: le.primaryEmail ?? null,
      parentId: opts.parentLegalEntityId,
    });

    // Branches are independent rows, so they go out concurrently — but in
    // capped batches rather than all at once, to keep a large hierarchy from
    // opening dozens of sockets against the gateway in one burst. A failing
    // batch aborts the rest; syncUsers is not attempted after a failure.
    for (let i = 0; i < branches.length; i += BRANCH_SYNC_BATCH_SIZE) {
      await Promise.all(
        branches.slice(i, i + BRANCH_SYNC_BATCH_SIZE).map((br) =>
          chatGatewaySyncService.syncBranch({
            id: br.id,
            legalEntityId: le.id,
            name: br.name,
            email: br.email ?? "",
            channelType: "EMAIL",
          }),
        ),
      );
    }

    await chatGatewaySyncService.syncUsers({
      legalEntityId: le.id,
      ...(opts.branchId ? { branchId: opts.branchId } : {}),
    });
  };

  const handleSave = async (opts: {
    id: string;
    legalEntityId: string;
    branchId: string | null;
    parentLegalEntityId: string | null;
    sourceCrm?: string | null;
    channelId?: string | null;
    enabledOverride?: boolean;
  }) => {
    const { id, legalEntityId, branchId, parentLegalEntityId, sourceCrm, channelId, enabledOverride } = opts;
    const cfg = getConfig(id);
    if (!hasRequiredFields(cfg)) {
      toast.error("Please enter a valid Email ID before saving.");
      return;
    }

    const emailId = cfg.emailId.trim();
    const inboxLabel = cfg.inboxLabel.trim() || null;
    const emailEnabled = enabledOverride !== undefined ? enabledOverride : cfg.enabled;

    const le = branchId ? null : findLegalEntity(legalEntityId);

    setSaving(id);
    setSuccess(null);
    // Save fans out to several sequential calls, so it blocks the page rather
    // than reporting progress in a floating panel.
    startPageLoading();
    try {
      // Mirror the WhatsApp save flow: verify historical-sync readiness first,
      // then upsert the channel, then refresh the hierarchy.
      const branchIdsToCheck = branchId
        ? [branchId]
        : (le ? getAllDescendantBranches(le) : []).map((b) => b.id);

      if (branchIdsToCheck.length > 0) {
        await crmGatewaySyncService.checkSyncReadiness({ branchIds: branchIdsToCheck });
      }

      // Exactly one Atlas channel row is written — the LE- or branch-scoped one
      // this panel owns.
      await communicationChannelsService.upsertEmail({
        channelType: "EMAIL",
        channelId: channelId ?? null,
        legalEntityId,
        branchId,
        sourceCrm: sourceCrm ?? null,
        emailEnabled,
        emailId,
        inboxLabel,
      });

      // Then the org structure: legal entity → every branch under this row →
      // users. Reported but not rethrown — the Atlas row is already persisted,
      // so a chat-gateway failure must not read as "save failed". Explicit Save
      // clicks only; the Active/Paused toggle reuses this handler.
      let structureSynced = true;
      if (enabledOverride === undefined) {
        try {
          await syncStructure({ legalEntityId, branchId, parentLegalEntityId });
        } catch (err) {
          structureSynced = false;
          reportError(
            err,
            "Email saved, but syncing the org structure failed",
            "Conversations gateway not updated",
          );
        }
      }

      patchConfig(id, {
        configured: true,
        enabled: emailEnabled,
        inboxLabel: cfg.inboxLabel.trim(),
        emailId,
      });

      const p = await profileService.refresh(email!, legalEntityId);
      setProfile(p);
      useProfileStore.getState().applyProfileData(p);
      setConfigs((prev) => mergeSeededConfigs(buildSeededConfigs(p), prev));

      if (structureSynced) {
        setSuccess(id);
        setTimeout(() => setSuccess(null), 3000);
        toast.success(
          enabledOverride !== undefined
            ? `Email ${emailEnabled ? "activated" : "paused"} successfully.`
            : "Email configuration saved and synced successfully.",
        );
      }
    } catch (err) {
      reportError(err, "Failed to save email configuration");
      // Revert optimistic toggle state if this was an explicit toggle.
      if (enabledOverride !== undefined) {
        patchConfig(id, { enabled: !enabledOverride });
      }
    } finally {
      setSaving(null);
      stopPageLoading();
    }
  };

  const handleConnect = async (id: string) => {
    setConnecting(id);
    try {
      // TODO(BE): kick off the email onboarding flow here.
      await new Promise((r) => setTimeout(r, 400));
      patchConfig(id, { connected: true });
    } catch (err) {
      reportError(err, "Failed to connect email");
    } finally {
      setConnecting(null);
    }
  };

  const getAllDescendantBranches = (entity: LegalEntityHierarchy): BranchHierarchy[] => [
    ...entity.branches,
    ...(entity.subEntities ?? []).flatMap(getAllDescendantBranches),
  ];

  const handleSyncForConversation = (opts: {
    id: string;
    legalEntityId: string;
    branchId: string | null;
    parentLegalEntityId: string | null;
    sourceCrm?: string | null;
    channelId?: string | null;
  }) => {
    const { id, legalEntityId, branchId, parentLegalEntityId, sourceCrm, channelId } = opts;
    const le = findLegalEntity(legalEntityId);
    if (!le) return;

    const branch = branchId
      ? getAllDescendantBranches(le).find((b) => b.id === branchId) ?? null
      : null;
    const branches = branch ? [branch] : getAllDescendantBranches(le);
    const cfg = getConfig(id);

    // Routes through the same channel-agnostic runner as WhatsApp; the email
    // channel step itself is still a BE TODO (see emailAdapter.syncChannel).
    startSync({
      channelType: "EMAIL",
      legalEntity: {
        id: le.id,
        name: le.name,
        primaryEmail: le.primaryEmail ?? null,
        parentId: parentLegalEntityId,
        sourceCrm: le.sourceCrm ?? sourceCrm ?? "atlas",
      },
      branches: branches.map((b) => ({ id: b.id, name: b.name, email: b.email ?? "" })),
      channel: {
        branchId,
        channelId: channelId ?? "",
        emailId: cfg.emailId.trim(),
        inboxLabel: cfg.inboxLabel.trim() || null,
      },
    });

    patchConfig(id, { synced: true, enabled: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-bridge-accent"></div>
      </div>
    );
  }

  /**
   * Renders the Email configuration form + action footer for a single entity
   * (LE or branch). Layout matches the WhatsApp panel — only the fields differ.
   */
  const renderConfigPanel = (opts: {
    id: string;
    name: string;
    kind: "LE" | "BR";
    isRootLegalEntity?: boolean;
    agencyRef?: string | null;
    legalEntityId: string;
    branchId: string | null;
    parentLegalEntityId: string | null;
    sourceCrm?: string | null;
    channelId?: string | null;
    chatwootAccountId?: string | number | null;
  }) => {
    const { id, name, kind, isRootLegalEntity, agencyRef, legalEntityId, branchId, parentLegalEntityId, sourceCrm, channelId, chatwootAccountId } = opts;
    const showInboxes = kind === "BR" || (kind === "LE" && isRootLegalEntity);
    const cfg = getConfig(id);
    const emailError = getEmailError(cfg.emailId);
    const canSave = hasRequiredFields(cfg);
    const isSyncDone = cfg.synced;
    const locked = cfg.configured;

    return (
      <div className="px-5 py-4 space-y-5">
        {!agencyRef && (
          <Link
            to="/integrations"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-700 hover:text-amber-900 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-md mb-2"
          >
            <Lock className="w-3 h-3" />
            Integrate CRM first to enable this section
          </Link>
        )}

        <div className="bg-slate-50/50 rounded-2xl border border-bridge-border p-4 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-bridge-border/40">
            <h4 className="text-xs font-semibold text-bridge-fg flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              Email Configuration
              {locked && (
                <Tooltip content={CHANNEL_LOCKED_MESSAGE} side="top">
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-bridge-border px-2 py-0.5 rounded-md">
                    <Lock className="w-3 h-3" />
                    Locked
                  </span>
                </Tooltip>
              )}
            </h4>
          </div>

          {/* ── General ─────────────────────────────────────────────── */}
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-5">
              <Field label="Email Inbox Label" hint="A friendly name to identify this inbox in Bridge AI.">
                {locked ? (
                  <LockedFieldValue value={cfg.inboxLabel} />
                ) : (
                  <Input
                    leading={<Mail className="h-4 w-4 text-bridge-muted/60" />}
                    placeholder="Support Inbox"
                    value={cfg.inboxLabel}
                    onChange={(e) => patchConfig(id, { inboxLabel: e.target.value })}
                  />
                )}
              </Field>

              <Field label="Email ID" required hint="The email address Bridge AI will use to send and receive emails.">
                {locked ? (
                  <LockedFieldValue value={cfg.emailId} />
                ) : (
                  <div className="flex flex-col gap-1 w-full">
                    <Input
                      leading={<AtSign className="h-4 w-4 text-bridge-muted/60" />}
                      placeholder="support@company.com"
                      type="email"
                      className={emailError ? "border-red-400 focus-within:border-red-500 ring-1 ring-red-200" : ""}
                      value={cfg.emailId}
                      autoComplete="off"
                      onChange={(e) => patchConfig(id, { emailId: e.target.value })}
                    />
                    {emailError && <div className="text-[11px] text-red-600 leading-tight">{emailError}</div>}
                  </div>
                )}
              </Field>
            </div>
          </div>
        </div>

        {/* ── Footer: actions ────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2.5 pt-1">
          {!locked && (
            <Button
              size="sm"
              variant="secondary"
              loading={saving === id}
              disabled={!canSave}
              onClick={() =>
                handleSave({ id, legalEntityId, branchId, parentLegalEntityId, sourceCrm, channelId })
              }
              className="rounded-xl h-9 px-4"
            >
              <Save className="h-4 w-4 shrink-0" />
              Save
            </Button>
          )}

          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-700 text-white rounded-xl h-9 px-4 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-green-600/50"
            loading={connecting === id}
            disabled={!cfg.configured || cfg.connected}
            title={cfg.connected ? "Email is already connected." : !cfg.configured ? "Save the configuration before connecting." : undefined}
            onClick={() => handleConnect(id)}
          >
            <ExternalLink className="h-4 w-4 shrink-0" />
            {cfg.connected ? "Connected" : "Connect"}
          </Button>

          {showInboxes && (
            <Button
              size="sm"
              variant="secondary"
              title={
                kind === "BR"
                  ? "Open this branch's inboxes in Conversations."
                  : "Open this legal entity's inboxes in Conversations."
              }
              onClick={async () => {
                const hasAccount = chatwootAccountId != null && `${chatwootAccountId}` !== "";
                const url = hasAccount
                  ? buildChatwootInboxesUrl(chatwootAccountId!)
                  : await fetchChatwootInboxesUrl(name);
                setChatwootTarget(url);
                navigate("/conversations");
              }}
              className="rounded-xl h-9 px-3.5"
            >
              <Inbox className="h-4 w-4 shrink-0" />
              Inboxes
            </Button>
          )}

          <Button
            size="sm"
            variant="secondary"
            disabled={!cfg.connected}
            title={!cfg.connected ? "Connect the email channel above to enable this." : undefined}
            onClick={() =>
              handleSyncForConversation({ id, legalEntityId, branchId, parentLegalEntityId, sourceCrm, channelId })
            }
            className="rounded-xl h-9 px-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCw className="h-4 w-4 shrink-0" />
            {isSyncDone ? "Resync" : "Enable Conversations"}
          </Button>

          {success === id && (
            <span className={`inline-flex items-center gap-1 text-xs font-bold text-green-600 ${isSyncDone ? "" : "ml-auto"}`}>
              <CheckCircle2 className="w-4 h-4" />
              Saved
            </span>
          )}

          {isSyncDone && (
            <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-200 ml-auto">
              <CheckCircle2 className="w-4 h-4" />
              Historical Data Synced
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderEmailEntityHeader = (opts: {
    id: string;
    name: string;
    typeLabel: string;
    variant: "le" | "branch";
    legalEntityId: string;
    branchId: string | null;
    parentLegalEntityId: string | null;
    sourceCrm?: string | null;
    channelId?: string | null;
  }) => {
    const { id, name, typeLabel, variant, legalEntityId, branchId, parentLegalEntityId, sourceCrm, channelId } =
      opts;
    const cfg = getConfig(id);
    const connected = cfg.synced && cfg.enabled;

    return (
      <ChannelEntityCardHeader
        name={name}
        typeLabel={typeLabel}
        variant={variant}
        expanded={isExpanded(id)}
        onToggleExpand={() => toggleExpanded(id)}
        configured={cfg.configured}
        connected={connected}
        enabled={cfg.enabled}
        toggleDisabled={!cfg.synced}
        toggleDisabledTitle="Enable conversations before activating this channel"
        onEnabledChange={(newVal) => {
          patchConfig(id, { enabled: newVal });
          handleSave({
            id,
            legalEntityId,
            branchId,
            parentLegalEntityId,
            sourceCrm,
            channelId,
            enabledOverride: newVal,
          });
        }}
        icon={
          variant === "le" ? (
            <Mail className="h-4 w-4 shrink-0 text-bridge-accent" />
          ) : (
            <Network className="h-3.5 w-3.5 shrink-0 text-bridge-muted" />
          )
        }
      />
    );
  };

  /** Recursive renderer. Depth drives indentation only; data flow unchanged. */
  const renderLegalEntityCard = (
    le: LegalEntityHierarchy,
    depth: number,
    parentLeId: string | null,
  ): React.ReactNode => (
    <React.Fragment key={le.id}>
      <div
        className="bg-white rounded-2xl border border-bridge-border overflow-hidden shadow-sm"
        style={{ marginLeft: depth * 24 }}
      >
        {/* ── LE header ────────────────────────────────────────────────── */}
        {renderEmailEntityHeader({
          id: le.id,
          name: le.name,
          typeLabel: depth === 0 ? "Legal Entity" : "Sub Legal Entity",
          variant: "le",
          legalEntityId: le.id,
          branchId: null,
          parentLegalEntityId: parentLeId,
          sourceCrm: le.sourceCrm,
          channelId: le.channelIds?.EMAIL,
        })}

        {/* ── LE config (form + actions) ───────────────────────────────── */}
        {isExpanded(le.id) &&
          renderConfigPanel({
            id: le.id,
            name: le.name,
            kind: "LE",
            isRootLegalEntity: depth === 0,
            agencyRef: le.agencyRef,
            legalEntityId: le.id,
            branchId: null,
            parentLegalEntityId: parentLeId,
            sourceCrm: le.sourceCrm,
            channelId: le.channelIds?.EMAIL,
            chatwootAccountId: le.chatwootAccountId,
          })}

        {/* ── Branches section ─────────────────────────────────────────── */}
        {(le.branches.length > 0 || (le.subEntities?.length ?? 0) > 0) && (
          <div className="border-t border-bridge-border bg-slate-50/30 px-5 py-2 flex items-center gap-2">
            <Network className="w-3.5 h-3.5 text-bridge-muted" />
            <span className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider">
              Branches ({le.branches.length})
            </span>
          </div>
        )}

        <div className="divide-y divide-bridge-border/50">
          {le.branches.map((branch) => (
            <div key={branch.id}>
              {renderEmailEntityHeader({
                id: branch.id,
                name: branch.name,
                typeLabel: "Branch",
                variant: "branch",
                legalEntityId: le.id,
                branchId: branch.id,
                parentLegalEntityId: parentLeId,
                sourceCrm: branch.sourceCrm || le.sourceCrm,
                channelId: branch.channelIds?.EMAIL,
              })}
              {isExpanded(branch.id) &&
                renderConfigPanel({
                  id: branch.id,
                  name: branch.name,
                  kind: "BR",
                  agencyRef: branch.agencyRef,
                  legalEntityId: le.id,
                  branchId: branch.id,
                  parentLegalEntityId: parentLeId,
                  sourceCrm: branch.sourceCrm || le.sourceCrm,
                  channelId: branch.channelIds?.EMAIL,
                  chatwootAccountId: branch.chatwootAccountId,
                })}
            </div>
          ))}
          {le.branches.length === 0 && (le.subEntities?.length ?? 0) === 0 && (
            <div className="px-5 py-3 text-[11px] text-bridge-muted italic">No branches configured for this legal entity.</div>
          )}
        </div>
      </div>

      {/* Sub-LEs (recursive). */}
      {le.subEntities?.map((sub) => renderLegalEntityCard(sub, depth + 1, le.id))}
    </React.Fragment>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3 flex gap-2.5 text-blue-800 text-xs">
        <Info className="w-4 h-4 shrink-0 text-blue-400" />
        <p>
          Configure Email to enable Bridge AI to send and receive emails from your support inbox. Add the inbox
          details below and click <strong>Save</strong>, then click <strong>Connect</strong>{" "}
          to begin the onboarding process. Use <strong>Enable Conversations</strong> once historical data has loaded.
        </p>
      </div>

      <div className="space-y-4">
        {displayEntities.map((le) => renderLegalEntityCard(le, 0, null))}
      </div>
    </div>
  );
};
